import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { UUID } from "crypto";

import { DocumentInfo } from "@interfaces/documents";
import { ApplicationMarketplaceResume, ApplicationResume } from "@interfaces/application";
import { DialogConfig, ButtonType, OptionApplicationMenu, DocumentAction, DialogType } from "@interfaces/slices";
import {
    ApplicationMarketplaceSelected,
    ApplicationSelected,
    DocumentGenerationStatus,
    PopupConfig,
    PopupType,
    Shareholder,
} from "@interfaces/slices";

export interface Components {
    applicationSelected: ApplicationSelected | ApplicationMarketplaceSelected;
    dialog: DialogConfig;
    popup: PopupConfig;
    buttonPanel: {
        buttons: Array<{ btn: ButtonType; disabled: boolean; onClick?: () => void; text?: string }>;
        buttonClicked: {
            btn: ButtonType | undefined;
            clicked: boolean;
        };
    };
    menuApplication: {
        optionSelected: OptionApplicationMenu;
        lock: boolean;
    };
    documentAction: {
        action: DocumentAction | undefined;
        doc: DocumentInfo | undefined;
    };
    printingProfileReport: {
        printingMode: boolean;
        module: {
            generalReady: boolean;
            downloadSatReady: boolean;
            profileReady: boolean;
            dashboardReady: boolean;
            financialReady: boolean;
            statusAlertsPldReady: boolean;
            statementsPldReady: boolean;
            resultsPldReady: boolean;
        };
    };
    shareholdersStructure: {
        shareholders: Array<Shareholder>;
        editingMode: boolean;
    };
    usersList: {
        editingMode: boolean;
        userSelected: {
            id: UUID;
            name: string;
            lastName: string;
            lastName2: string;
        };
    };
    opinionModule: {
        formData: {
            publicDeedNumber: string;
            publicDeedDate: string;
            notaryOfficeNumber: string;
            notaryName: string;
            municipality: string;
            federative: string;
            sufficientPowers: boolean | null;
            limitationContractCredit: boolean | null;
            commissionerName: string;
            commissionerLastName: string;
            commissionerLastName2: string;
            majorityShareholderValidate: boolean | null;
            legalAgentName: string;
            legalAgentLastName: string;
            legalAgentLastName2: string;
            legalAgentIsShareholder: boolean | null;
            legalAgentCharge: string;
            legalAgentChargeOption: number;
            comercialFolioNo: string;
            legalAgentRepresentationTypeOption: UUID;
            legalAgentRfc: string;
        };
        legalAgentId: UUID;
        majorityShareholderId: UUID;
    };
    backdrop: {
        visible: boolean;
    };
    currentSection: {
        module: string;
        section: string;
        reload: boolean;
    };
    statusContractualDocs: Array<{
        code: string;
        status: DocumentGenerationStatus;
        errors: Array<string>;
    }>;
    layout: {
        sidebar: {
            expandState: boolean;
        };
    };
    scoreIdentidad: number | null;
    /**
     * Entidad legal seleccionada en la franja "Cadena societaria" de Dictamen
     * Legal (`DictamenLegalV2`). `null` = usar la raíz (el `Ruling`/`ClientDetails`
     * de la Application, es decir `applicationSelected.*`) — el caso de siempre
     * para Applications sin cadena societaria. Se resetea a `null` en
     * `setApplicationSelected` para no arrastrar la selección de una Application
     * a otra.
     */
    selectedDictamenEntity: {
        rulingId: UUID;
        clientDetailsId: UUID;
        personId: UUID | null;
        companyRfc: string;
        companyName: string;
    } | null;
}

const initialState: Components = {
    applicationSelected: {
        id: "0-0-0-0-0",
        id_application: 0,
        company_name: "",
        comercial_name: "",
        company_rfc: "",
        user_name: "",
        user_email: "",
        requested_amount: 0,
        requested_term: 0,
        approved_amount: 0,
        approved_term: 0,
        approved_min_term: 0,
        approved_max_term: 0,
        screen_time_seconds: 0,
        total_time_seconds: 0,
        client_total_time_seconds: 0,
        last_status_date: "",
        stage_name: "",
        substage_name: "",
        status_name: "",
        status_code: "",
        channel: "",
        channel_id: "",
        detail_channel: "",
        macrochannel: "",
        assigned_partner: "",
        assigned_partner_email: "",
        interested: false,
        msol_total_comments: 0,
        mrev_total_comments: 0,
        mpin_total_comments: 0,
        mrec_total_comments: 0,
        maut_total_comments: 0,
        mcli_total_comments: 0,
        authorized: false,
        credit_validated: false,
        pld_validated: false,
        legal_incidents_validated: false,
        client_detail_id: "0-0-0-0-0",
        stage: {
            id: "0-0-0-0-0",
            name: "",
            description: "",
        },
        substage: {
            id: "0-0-0-0-0",
            name: "",
            description: "",
            stage_id: "0-0-0-0-0",
        },
        status: {
            id: "0-0-0-0-0",
            name: "",
            description: "",
            substage_id: "0-0-0-0-0",
            code: "",
        },
        tax_system: {
            id: "0-0-0-0-0",
            name: "",
            code: "",
            product_id: "0-0-0-0-0",
            tax_system_type: "",
            application_code: "",
            config_extra: {
                with_leasing: false,
                with_guarantee: false,
                with_credit_bureau: false,
                show_ia_resume: {
                    credit_profile: false,
                    credit_bureau: false,
                    visit: false,
                },
            },
        },
        accept: null,
        declined: null,
        financial_company_id: "0-0-0-0-0",
        financial_company: {
            id: "0-0-0-0-0",
            code: "",
            name: "",
            logo: "",
            description: "",
            contact_name: "",
            contact_email: "",
            offer_config: {
                authorizedFee: 0,
                openingFee: 0,
                validity: {
                    options: [],
                    type: "",
                },
            },
            active: false,
        },
        dictamenCompletado: false,
        company_docs_validated: false,
        shareholder_docs_validated: false,
        owners_docs_validated: false,
        existsSDC: false,
        visit_analysis_validated: null,
        check_control_validated: null,
        disburment_date: "",
        product_id: "0-0-0-0-0",
        financed_service_code: "",
        guarantee_type_code: "",
        company_source: "",
        id_application_company: 0,
        company_id: "0-0-0-0-0",
        with_leasing: false,
        with_guarantee: false,
        with_credit_bureau: false,
        loan_application_parent_immediate_id: "0-0-0-0-0",
        user_bpm_id: "0-0-0-0-0",
        user_bpm: {
            id: "0-0-0-0-0",
            name: "",
            last_name: "",
            last_name_2: "",
        },
    },
    dialog: {
        shown: false,
        type: DialogType.NONE,
        props: undefined,
        onRejectClick: () => {},
        onAcceptClick: () => {},
        onCloseClick: () => {},
    },
    popup: {
        type: PopupType.NONE,
        props: undefined,
        anchorEl: undefined,
    },
    buttonPanel: {
        buttons: [],
        buttonClicked: {
            clicked: false,
            btn: undefined,
        },
    },
    menuApplication: {
        optionSelected: OptionApplicationMenu.INFO_GRAL,
        lock: false,
    },
    documentAction: {
        action: DocumentAction.SEE,
        doc: undefined,
    },
    printingProfileReport: {
        printingMode: false,
        module: {
            generalReady: false,
            downloadSatReady: false,
            profileReady: false,
            dashboardReady: false,
            financialReady: false,
            statusAlertsPldReady: false,
            statementsPldReady: false,
            resultsPldReady: false,
        },
    },
    shareholdersStructure: {
        shareholders: [],
        editingMode: false,
    },
    usersList: {
        editingMode: false,
        userSelected: {
            id: "0-0-0-0-0",
            name: "",
            lastName: "",
            lastName2: "",
        },
    },
    opinionModule: {
        formData: {
            publicDeedNumber: "",
            publicDeedDate: "",
            notaryOfficeNumber: "",
            notaryName: "",
            municipality: "",
            federative: "",
            sufficientPowers: null,
            limitationContractCredit: null,
            commissionerName: "",
            commissionerLastName: "",
            commissionerLastName2: "",
            majorityShareholderValidate: null,
            legalAgentName: "",
            legalAgentLastName: "",
            legalAgentLastName2: "",
            legalAgentIsShareholder: null,
            legalAgentCharge: "",
            legalAgentChargeOption: 0,
            comercialFolioNo: "",
            legalAgentRepresentationTypeOption: "0-0-0-0-0",
            legalAgentRfc: "",
        },
        legalAgentId: "0-0-0-0-0",
        majorityShareholderId: "0-0-0-0-0",
    },
    backdrop: {
        visible: false,
    },
    currentSection: {
        module: "",
        section: "",
        reload: false,
    },
    statusContractualDocs: [],
    layout: {
        sidebar: {
            expandState: true,
        },
    },
    scoreIdentidad: null,
    selectedDictamenEntity: null,
    // lists: {
    //     current: "",
    //     shared_filters: {
    //         companies: [],
    //         financials: {
    //             id: "0-0-0-0-0",
    //             code: "",
    //             name: "",
    //         },
    //         fullSearch: {
    //             text: "",
    //             searching: false,
    //         },
    //     },
    //     configList: [],
    // },
};

export const componentsSlice = createSlice({
    name: "components",
    initialState,
    reducers: {
        setApplicationSelected: {
            reducer: (state, action: PayloadAction<ApplicationResume | ApplicationMarketplaceResume>) => {
                state.applicationSelected = {
                    ...action.payload,
                    dictamenCompletado: false,
                    company_docs_validated: false,
                    shareholder_docs_validated: false,
                    owners_docs_validated: false,
                    existsSDC: false,
                    // credit_validated: null,
                    // pld_validated: null,
                    // legal_incidents_validated: null,
                    visit_analysis_validated: null,
                    check_control_validated: null,
                };
                state.selectedDictamenEntity = null;
            },
            prepare: (
                payload: ApplicationResume | ApplicationMarketplaceResume = {
                    id: "0-0-0-0-0",
                    id_application: 0,
                    company_name: "",
                    comercial_name: "",
                    company_rfc: "",
                    user_name: "",
                    user_email: "",
                    requested_amount: 0,
                    requested_term: 0,
                    approved_amount: 0,
                    approved_term: 0,
                    approved_min_term: 0,
                    approved_max_term: 0,
                    screen_time_seconds: 0,
                    total_time_seconds: 0,
                    client_total_time_seconds: 0,
                    last_status_date: "",
                    stage_name: "",
                    substage_name: "",
                    status_name: "",
                    status_code: "",
                    channel: "",
                    channel_id: "",
                    detail_channel: "",
                    macrochannel: "",
                    assigned_partner: "",
                    assigned_partner_email: "",
                    interested: false,
                    disburment_date: "",
                    msol_total_comments: 0,
                    mrev_total_comments: 0,
                    mpin_total_comments: 0,
                    mrec_total_comments: 0,
                    maut_total_comments: 0,
                    mcli_total_comments: 0,
                    authorized: false,
                    credit_validated: false,
                    legal_incidents_validated: false,
                    pld_validated: false,
                    financed_service_code: "",
                    guarantee_type_code: "",
                    company_source: "",
                    with_guarantee: false,
                    with_leasing: false,
                    client_detail_id: "0-0-0-0-0",
                    product_id: "0-0-0-0-0",
                    accept: null,
                    declined: null,
                    financial_company_id: "0-0-0-0-0",
                    financial_company: {
                        id: "0-0-0-0-0",
                        code: "",
                        name: "",
                        logo: "",
                        description: "",
                        contact_name: "",
                        contact_email: "",
                        offer_config: {
                            formalizationTime: 0,
                            authorizedFee: 0,
                            openingFee: 0,
                            validity: {
                                type: "",
                                options: [],
                            },
                        },
                        active: false,
                    },
                    stage: {
                        id: "0-0-0-0-0",
                        name: "",
                        description: "",
                    },
                    substage: {
                        id: "0-0-0-0-0",
                        name: "",
                        description: "",
                        stage_id: "0-0-0-0-0",
                    },
                    status: {
                        id: "0-0-0-0-0",
                        name: "",
                        description: "",
                        substage_id: "0-0-0-0-0",
                        code: "",
                    },
                    tax_system: {
                        id: "0-0-0-0-0",
                        name: "",
                        code: "",
                        product_id: "0-0-0-0-0",
                        tax_system_type: "",
                        application_code: "",
                        config_extra: {
                            with_leasing: false,
                            with_guarantee: false,
                            with_credit_bureau: false,
                            bank_information_config: {
                                aval: { min_statements: 0, total_statements: 0 },
                                company: { min_statements: 0, total_statements: 0 },
                                second_aval: { min_statements: 0, total_statements: 0 },
                            },
                            optional_internal_signature: false,
                            show_ia_resume: {
                                credit_profile: false,
                                credit_bureau: false,
                                visit: false,
                            },
                        },
                    },
                    id_application_company: 0,
                    company_id: "0-0-0-0-0",
                },
            ) => {
                return {
                    payload: payload,
                };
            },
        },
        setCleanApplicationSelected: (state) => {
            state.applicationSelected = initialState.applicationSelected;
        },
        setApplicationCompleted: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.dictamenCompletado = action.payload;
        },
        setApplicationState: (state, action: PayloadAction<{ code: string; name: string; statusId: UUID }>) => {
            //   state.applicationSelected.statusId = action.payload.statusId;
            state.applicationSelected.status_code = action.payload.code;
            //   state.applicationSelected.estatus = action.payload.estatus;
            state.applicationSelected.status_name = action.payload.name;
            state.applicationSelected.status.code = action.payload.code;
            state.applicationSelected.status.name = action.payload.name;
            state.applicationSelected.status.id = action.payload.statusId;
        },
        setApplicationStateCode: (state, action: PayloadAction<string>) => {
            state.applicationSelected.status_code = action.payload;
        },
        setCompanyDocsValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.company_docs_validated = action.payload;
        },
        setShareHolderDocsValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.shareholder_docs_validated = action.payload;
        },
        setOwnersDocsValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.owners_docs_validated = action.payload;
        },
        setExistSDC: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.existsSDC = action.payload;
        },
        setApplicationCreditValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.credit_validated = action.payload;
        },
        setApplicationPldValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.pld_validated = action.payload;
        },
        setApplicationLegalIncidentValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.legal_incidents_validated = action.payload;
        },
        setApplicationVisitAnalysisValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.visit_analysis_validated = action.payload;
        },
        setApplicationCheckControlValidated: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.check_control_validated = action.payload;
        },
        setApplicationCanalIdUpdated: (state, action: PayloadAction<string>) => {
            state.applicationSelected.channel_id = action.payload;
        },
        setApplicationUserBpmUpdated: (
            state,
            action: PayloadAction<{ id: UUID; name: string; last_name: string; last_name_2: string }>,
        ) => {
            state.applicationSelected.user_bpm_id = action.payload.id;
            state.applicationSelected.user_bpm = action.payload;
        },
        setApplicationGuaranteeTypeChanged: (state, action: PayloadAction<string>) => {
            state.applicationSelected.guarantee_type_code = action.payload;
        },
        setApplicationGuaranteeTypesChanged: (state, action: PayloadAction<Array<UUID>>) => {
            state.applicationSelected.guarantee_type_ids = action.payload;
        },
        setApplicationWithLeasing: (state, action: PayloadAction<boolean>) => {
            state.applicationSelected.with_leasing = action.payload;
        },
        setApplicationTaxSystemGuarantee: (
            state,
            action: PayloadAction<{ withGuarantee: boolean; taxSystem: ApplicationResume["tax_system"] }>,
        ) => {
            state.applicationSelected.with_guarantee = action.payload.withGuarantee;
            state.applicationSelected.tax_system = action.payload.taxSystem;
        },
        setDialogConfig: {
            reducer: (state, action: PayloadAction<DialogConfig>) => {
                state.dialog = action.payload;
            },
            prepare: (
                payload: DialogConfig = {
                    shown: false,
                    type: DialogType.NONE,
                    props: undefined,
                },
            ) => {
                return {
                    payload: payload,
                };
            },
        },
        setDialogConfigProcessing: (state, action: PayloadAction<boolean>) => {
            state.dialog.isProcessing = action.payload;
        },
        setVisibleButtons: (
            state,
            action: PayloadAction<Array<{ btn: ButtonType; disabled: boolean; onClick?: () => void; text?: string }>>,
        ) => {
            state.buttonPanel.buttons = action.payload;
        },
        setOptionAppSelected: (state, action: PayloadAction<OptionApplicationMenu>) => {
            state.menuApplication.optionSelected = action.payload;
        },
        setLockMenuApplication: (state, action: PayloadAction<boolean>) => {
            state.menuApplication.lock = action.payload;
        },
        setDocumentAction: (state, action: PayloadAction<{ action: DocumentAction; doc: DocumentInfo }>) => {
            state.documentAction = action.payload;
        },
        setClearDocumentAction: (state) => {
            state.documentAction = {
                action: DocumentAction.NONE,
                doc: {
                    url: "",
                    created_at: "",
                    updated_at: "",
                    category_code: "",
                    category_name: "",
                    file_name: "",
                    validated: undefined,
                    status: false,
                    sign_client: undefined,
                    sign_user: undefined,
                    sign_validated: undefined,
                },
            };
        },
        setButtonClicked: (state, action: PayloadAction<{ btn: ButtonType | undefined; clicked: boolean }>) => {
            state.buttonPanel.buttonClicked = action.payload;
        },
        setPrintingMode: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.printingMode = action.payload;
        },
        setPrintingGeneralReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.generalReady = action.payload;
        },
        setPrintingDownloadSatReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.downloadSatReady = action.payload;
        },
        setPrintingProfileReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.profileReady = action.payload;
        },
        setPrintingDashboardReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.dashboardReady = action.payload;
        },
        setPrintingFinancialReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.financialReady = action.payload;
        },
        setStatusAlertsPldReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.statusAlertsPldReady = action.payload;
        },
        setStatementsPldReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.statementsPldReady = action.payload;
        },
        setResultsPldReady: (state, action: PayloadAction<boolean>) => {
            state.printingProfileReport.module.resultsPldReady = action.payload;
        },
        setPrintingReset: (state) => {
            state.printingProfileReport = {
                printingMode: false,
                module: {
                    generalReady: false,
                    downloadSatReady: false,
                    profileReady: false,
                    dashboardReady: false,
                    financialReady: false,
                    statusAlertsPldReady: false,
                    statementsPldReady: false,
                    resultsPldReady: false,
                },
            };
        },
        setShareholders: (state, action: PayloadAction<Array<Shareholder>>) => {
            state.shareholdersStructure.shareholders = action.payload;
        },
        setShareholderEditingMode: (state, action: PayloadAction<boolean>) => {
            state.shareholdersStructure.editingMode = action.payload;
        },
        setPopupConfig: {
            reducer: (state, action: PayloadAction<PopupConfig>) => {
                state.popup = action.payload;
            },
            prepare: (
                payload: PopupConfig = {
                    type: PopupType.NONE,
                    props: undefined,
                    anchorEl: undefined,
                    placement: "auto",
                },
            ) => {
                return {
                    payload: payload,
                };
            },
        },
        setUsersListEditingMode: (state, action: PayloadAction<boolean>) => {
            state.usersList.editingMode = action.payload;
        },
        setUserListSelected: (state, action: PayloadAction<any | undefined>) => {
            if (action.payload === undefined) {
                state.usersList.userSelected = initialState.usersList.userSelected;
            } else {
                state.usersList.userSelected = action.payload;
            }
        },
        setOpinionModuleValues: (state, action: PayloadAction<any>) => {
            state.opinionModule.formData = action.payload;
        },
        setOpinionModuleLegalAgentId: (state, action: PayloadAction<UUID>) => {
            state.opinionModule.legalAgentId = action.payload;
        },
        setOpinionModuleMajorityShareholder: (state, action: PayloadAction<UUID>) => {
            state.opinionModule.majorityShareholderId = action.payload;
        },
        setVisibleBackdrop: (state, action: PayloadAction<boolean>) => {
            state.backdrop.visible = action.payload;
        },
        setCurrentModule: (state, action: PayloadAction<string>) => {
            state.currentSection.module = action.payload;
        },
        setCurrentSection: (state, action: PayloadAction<string>) => {
            state.currentSection.section = action.payload;
        },
        setClearStatusContractualDocs: (state, _: PayloadAction<void>) => {
            state.statusContractualDocs = [];
        },
        setStatusContractualDocs: (
            state,
            action: PayloadAction<{ code: string; status: DocumentGenerationStatus; errors: Array<string> }>,
        ) => {
            const exists = state.statusContractualDocs.find((t) => t.code === action.payload.code);
            if (exists) {
                state.statusContractualDocs = state.statusContractualDocs.map((t) =>
                    t.code === action.payload.code ? action.payload : t,
                );
            } else {
                state.statusContractualDocs.push(action.payload);
            }
        },
        setExpandStateLayoutSidebar: (state, action: PayloadAction<boolean>) => {
            state.layout.sidebar.expandState = action.payload;
        },
        setReloadSection: (state, action: PayloadAction<boolean>) => {
            state.currentSection.reload = action.payload;
        },
        setScoreIdentidad: (state, action: PayloadAction<number | null>) => {
            state.scoreIdentidad = action.payload;
        },
        setApplicationUserEmailUpdated: (state, action: PayloadAction<string>) => {
            state.applicationSelected.user_email = action.payload;
        },
        setSelectedDictamenEntity: (state, action: PayloadAction<Components["selectedDictamenEntity"]>) => {
            state.selectedDictamenEntity = action.payload;
        },
        resetComponents: () => initialState,
    },
});

export const {
    setCleanApplicationSelected,
    setApplicationSelected,
    setApplicationCompleted,
    setApplicationStateCode,
    setCompanyDocsValidated,
    setShareHolderDocsValidated,
    setOwnersDocsValidated,
    setExistSDC,
    setApplicationCreditValidated,
    setApplicationWithLeasing,
    setApplicationTaxSystemGuarantee,
    setApplicationPldValidated,
    setApplicationLegalIncidentValidated,
    setApplicationVisitAnalysisValidated,
    setSelectedDictamenEntity,
    setApplicationCheckControlValidated,
    setApplicationCanalIdUpdated,
    setApplicationUserBpmUpdated,
    setApplicationGuaranteeTypeChanged,
    setApplicationGuaranteeTypesChanged,
    setDialogConfig,
    setDialogConfigProcessing,
    setVisibleButtons,
    setOptionAppSelected,
    setLockMenuApplication,
    setDocumentAction,
    setClearDocumentAction,
    setButtonClicked,
    setPrintingMode,
    setPrintingGeneralReady,
    setPrintingDownloadSatReady,
    setPrintingProfileReady,
    setPrintingDashboardReady,
    setPrintingFinancialReady,
    setResultsPldReady,
    setStatementsPldReady,
    setStatusAlertsPldReady,
    setPrintingReset,
    setShareholders,
    setShareholderEditingMode,
    setPopupConfig,
    setUsersListEditingMode,
    setUserListSelected,
    setOpinionModuleValues,
    setOpinionModuleLegalAgentId,
    setOpinionModuleMajorityShareholder,
    setVisibleBackdrop,
    setCurrentModule,
    setCurrentSection,
    setStatusContractualDocs,
    setClearStatusContractualDocs,
    setExpandStateLayoutSidebar,
    setReloadSection,
    setApplicationUserEmailUpdated,
    setScoreIdentidad,
    resetComponents,
} = componentsSlice.actions;
export default componentsSlice.reducer;
